/** @jsxImportSource @emotion/react */
import { useState } from "react"
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box } from "@mui/material"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { ColorPickerDialog } from "./components/ColorPickerDialog"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const DescriptionStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 1rem;
    color: ${theme.palette.text.primary};
    opacity: 0.7;
    max-width: 450px;

    ${mobileCss(`
        font-size: 0.9rem;
    `)}
`)

const ButtonsContainerStyle = withMyTheme(() => css`
    display: flex;
    gap: 2.5rem;
    width: 100%;
    justify-content: center;
    
    ${mobileCss(`
        gap: 1.5rem;
    `)}
`)

const ColorButtonStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.05);
    }

    &:active {
        transform: scale(0.97);
    }
`)

const ColorSwatchButtonStyle = (color: string) => withMyTheme((theme) => css`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: ${color};
    border: 3px solid ${theme.palette.primary.main};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transition: background-color 0.3s ease, box-shadow 0.2s ease;
    cursor: pointer;

    &:hover {
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
    }

    ${mobileCss(`
        width: 70px;
        height: 70px;
    `)}
`)

const ColorLabelStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 0.9rem;
    font-weight: 500;
    color: ${theme.palette.text.primary};
`)

const ColorHexStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 0.8rem;
    color: ${theme.palette.text.primary};
    opacity: 0.5;
    text-transform: uppercase;
`)

const PreviewStyle = withMyTheme(() => css`
    display: flex;
    gap: 0;
    border-radius: 16px;
    overflow: hidden;
    height: 60px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

    ${mobileCss(`
        max-width: 280px;
        height: 50px;
    `)}
`)

type ActivePicker = 'primary' | 'secondary' | null

export const ColorPickerStep = () => {
    const { t } = useTranslation()
    const { formData, updateColors } = useQuestionnaireForm()
    const [activePicker, setActivePicker] = useState<ActivePicker>(null)

    const handleConfirm = (color: string) => {
        if (activePicker === 'primary') {
            updateColors({ primaryColor: color })
        } else if (activePicker === 'secondary') {
            updateColors({ secondaryColor: color })
        }
    }

    const activeColor = activePicker === 'primary'
        ? formData.colors.primaryColor
        : formData.colors.secondaryColor

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.colors.title')}
            </Typography>
            <Typography css={DescriptionStyle}>
                {t('questionnaire.colors.description')}
            </Typography>

            <Box css={ButtonsContainerStyle}>
                <div css={ColorButtonStyle} onClick={() => setActivePicker('primary')}>
                    <div css={ColorSwatchButtonStyle(formData.colors.primaryColor)} />
                    <span css={ColorLabelStyle}>{t('questionnaire.colors.primaryLabel')}</span>
                    <span css={ColorHexStyle}>{formData.colors.primaryColor}</span>
                </div>
                <div css={ColorButtonStyle} onClick={() => setActivePicker('secondary')}>
                    <div css={ColorSwatchButtonStyle(formData.colors.secondaryColor)} />
                    <span css={ColorLabelStyle}>{t('questionnaire.colors.secondaryLabel')}</span>
                    <span css={ColorHexStyle}>{formData.colors.secondaryColor}</span>
                </div>
            </Box>

            <Box css={PreviewStyle}>
                <div style={{
                    flex: 1,
                    backgroundColor: formData.colors.primaryColor,
                    transition: 'background-color 0.3s ease'
                }} />
                <div style={{
                    flex: 1,
                    backgroundColor: formData.colors.secondaryColor,
                    transition: 'background-color 0.3s ease'
                }} />
            </Box>

            <ColorPickerDialog
                open={activePicker !== null}
                color={activeColor}
                onConfirm={handleConfirm}
                onClose={() => setActivePicker(null)}
            />
        </div>
    )
}
